import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchGSTBalances,
  recordGSTPayment,
  fetchGSTPaymentHistory
} from '../../store/gstSettlementStore/gstSettlementThunk';
import { resetPaymentStatus } from '../../store/gstSettlementStore/gstSettlementSlice';
import {
  fetchTDSBalances,
  recordTDSPayment,
  fetchTDSPaymentHistory
} from '../../store/tdsSettlementStore/tdsSettlementThunk';
import { resetPaymentStatus as resetTDSPaymentStatus } from '../../store/tdsSettlementStore/tdsSettlementSlice';
import {
  fetchTCSBalances,
  recordTCSPayment,
  fetchTCSPaymentHistory
} from '../../store/tcsSettlementStore/tcsSettlementThunk';
import { resetPaymentStatus as resetTCSPaymentStatus } from '../../store/tcsSettlementStore/tcsSettlementSlice';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { ApiStatus } from '../../utils/api.types';
import { IGSTPaymentForm } from '../../utils/gstSettlement.types';
import { ITDSPaymentForm } from '../../utils/tdsSettlement.types';
import { ITCSPaymentForm } from '../../utils/tcsSettlement.types';
import { Utils } from '../../utils/common-utils';
import { format } from 'date-fns';
import GSTPaymentDialog from './GSTPaymentDialog';
import TDSPaymentDialog from './TDSPaymentDialog';
import TCSPaymentDialog from './TCSPaymentDialog';
import TDSTransactionSummaryModal from './TDSTransactionSummaryModal';
import TCSTransactionSummaryModal from './TCSTransactionSummaryModal';

const GSTSettlement: React.FC = () => {
  const dispatch = useAppDispatch();

  // Redux state - GST
  const { balances, payments, balancesStatus, paymentStatus, historyStatus } = useAppSelector(
    (state) => state.gstSettlement
  );
  // Redux state - TDS
  const { balances: tdsBalances, payments: tdsPayments, balancesStatus: tdsBalancesStatus, paymentStatus: tdsPaymentStatus, historyStatus: tdsHistoryStatus } = useAppSelector(
    (state) => state.tdsSettlement
  );
  // Redux state - TCS
  const { balances: tcsBalances, payments: tcsPayments, balancesStatus: tcsBalancesStatus, paymentStatus: tcsPaymentStatus, historyStatus: tcsHistoryStatus } = useAppSelector(
    (state) => state.tcsSettlement
  );
  const { selectedAccount } = useAppSelector((state) => state.auth);

  // Get financial year dates
  const { startDateFY, endDateFY } = Utils.getFinancialYearDates();

  // Local state
  const [dateRange, setDateRange] = useState({
    startDate: startDateFY,
    endDate: endDateFY
  });
  const [selectedDateRangeValue, setSelectedDateRangeValue] = useState('This FY Year');
  const [showDateRange, setShowDateRange] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openTDSPaymentDialog, setOpenTDSPaymentDialog] = useState(false);
  const [openTCSPaymentDialog, setOpenTCSPaymentDialog] = useState(false);
  const [openTDSTransactionModal, setOpenTDSTransactionModal] = useState(false);
  const [openTCSTransactionModal, setOpenTCSTransactionModal] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [paymentForm, setPaymentForm] = useState<IGSTPaymentForm>({
    paymentPeriod: {
      month: '',
      fromDate: '',
      toDate: '',
      gstrPeriod: ''
    },
    returnDetails: {
      taxpayerType: 'REGULAR',
      returnType: 'GSTR-3B',
      paymentFrequency: 'MONTHLY'
    },
    cgstPaid: 0,
    sgstPaid: 0,
    igstPaid: 0,
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    challanNumber: '',
    paymentMode: 'NEFT',
    bankAccount: '',
    referenceNumber: '',
    remarks: ''
  });
  const [tdsPaymentForm, setTDSPaymentForm] = useState<ITDSPaymentForm>({
    paymentPeriod: {
      month: '',
      fromDate: '',
      toDate: '',
      period: ''
    },
    returnDetails: {
      section: '194J',
      formType: '26Q',
      deductorType: 'COMPANY'
    },
    tdsPayable: 0,
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    challanNumber: '',
    bsrCode: '',
    assessmentYear: '',
    paymentMode: 'NEFT',
    bankAccount: '',
    referenceNumber: '',
    remarks: ''
  });
  const [tcsPaymentForm, setTCSPaymentForm] = useState<ITCSPaymentForm>({
    paymentPeriod: {
      month: '',
      fromDate: '',
      toDate: '',
      period: ''
    },
    returnDetails: {
      section: '206C',
      formType: '27EQ',
      collectorType: 'COMPANY'
    },
    tcsPayable: 0,
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    challanNumber: '',
    bsrCode: '',
    assessmentYear: '',
    paymentMode: 'NEFT',
    bankAccount: '',
    referenceNumber: '',
    remarks: ''
  });

  // Fetch bank accounts (for bank account dropdown)
  useEffect(() => {
    const getBanks = async () => {
      if (selectedAccount?._id) {
        try {
          const result: any = await dispatch(fetchBanks(selectedAccount._id));
          if (result.payload?.banks) {
            setBankAccounts(result.payload.banks.map((bank: any) => ({
              _id: bank._id,
              ledgerName: `${bank.accountName} - ${bank.bankName}`
            })));
          }
        } catch (error) {
          console.error('Error fetching banks:', error);
        }
      }
    };
    getBanks();
  }, [dispatch, selectedAccount]);

  // Fetch balances when dates change
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const toDate = format(dateRange.endDate, 'yyyy-MM-dd');
      dispatch(fetchGSTBalances({ fromDate, toDate }));
      dispatch(fetchTDSBalances({ fromDate, toDate }));
      dispatch(fetchTCSBalances({ fromDate, toDate }));
    }
  }, [dateRange, dispatch]);

  // Fetch payment history on mount
  useEffect(() => {
    dispatch(fetchGSTPaymentHistory());
    dispatch(fetchTDSPaymentHistory());
    dispatch(fetchTCSPaymentHistory());
  }, [dispatch]);

  // Reset payment status when dialog closes - GST
  useEffect(() => {
    if (paymentStatus === ApiStatus.SUCCESS && openPaymentDialog) {
      setOpenPaymentDialog(false);
      dispatch(resetPaymentStatus());
      // Refresh balances and history
      if (dateRange.startDate && dateRange.endDate) {
        const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
        const toDate = format(dateRange.endDate, 'yyyy-MM-dd');
        dispatch(fetchGSTBalances({ fromDate, toDate }));
      }
      dispatch(fetchGSTPaymentHistory());
    }
  }, [paymentStatus, openPaymentDialog, dispatch, dateRange]);

  // Reset payment status when dialog closes - TDS
  useEffect(() => {
    if (tdsPaymentStatus === ApiStatus.SUCCESS && openTDSPaymentDialog) {
      setOpenTDSPaymentDialog(false);
      dispatch(resetTDSPaymentStatus());
      // Refresh balances and history
      if (dateRange.startDate && dateRange.endDate) {
        const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
        const toDate = format(dateRange.endDate, 'yyyy-MM-dd');
        dispatch(fetchTDSBalances({ fromDate, toDate }));
      }
      dispatch(fetchTDSPaymentHistory());
    }
  }, [tdsPaymentStatus, openTDSPaymentDialog, dispatch, dateRange]);

  // Reset payment status when dialog closes - TCS
  useEffect(() => {
    if (tcsPaymentStatus === ApiStatus.SUCCESS && openTCSPaymentDialog) {
      setOpenTCSPaymentDialog(false);
      dispatch(resetTCSPaymentStatus());
      // Refresh balances and history
      if (dateRange.startDate && dateRange.endDate) {
        const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
        const toDate = format(dateRange.endDate, 'yyyy-MM-dd');
        dispatch(fetchTCSBalances({ fromDate, toDate }));
      }
      dispatch(fetchTCSPaymentHistory());
    }
  }, [tcsPaymentStatus, openTCSPaymentDialog, dispatch, dateRange]);

  const handleFetchBalances = () => {
    if (dateRange.startDate && dateRange.endDate) {
      const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const toDate = format(dateRange.endDate, 'yyyy-MM-dd');
      dispatch(fetchGSTBalances({ fromDate, toDate }));
    }
  };

  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'startDate') {
        setDateRange({ ...dateRange, startDate: newValue });
      } else if (name === 'endDate') {
        setDateRange({ ...dateRange, endDate: newValue });
      }
    }
  };

  const handleDateRange = (e: any) => {
    const _dateFilterName = e.target.value;
    setSelectedDateRangeValue(_dateFilterName);

    if (e.target.value && e.target.value !== '') {
      if (_dateFilterName === 'This Month') {
        setShowDateRange(false);
        const date = new Date();
        const y = date.getFullYear();
        const m = date.getMonth();
        setDateRange({
          startDate: new Date(y, m, 1),
          endDate: new Date(y, m + 1, 0)
        });
      } else if (_dateFilterName === 'Last Month') {
        setShowDateRange(false);
        const date = new Date();
        const y = date.getFullYear();
        const m = date.getMonth();
        setDateRange({
          startDate: new Date(y, m - 1, 1),
          endDate: new Date(y, (m - 1) + 1, 0)
        });
      } else if (_dateFilterName === 'This FY Year') {
        setShowDateRange(false);
        setDateRange({
          startDate: startDateFY,
          endDate: endDateFY
        });
      } else if (_dateFilterName === 'Last FY Year') {
        setShowDateRange(false);
        const { startDateLastFY, endDateLastFY } = Utils.getLastFinancialYearDates();
        setDateRange({
          startDate: startDateLastFY,
          endDate: endDateLastFY
        });
      } else if (_dateFilterName === 'Custom') {
        setShowDateRange(true);
      }
    }
  };

  const handleOpenPaymentDialog = () => {
    if (!balances) {
      return;
    }

    const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
    const toDate = format(dateRange.endDate, 'yyyy-MM-dd');

    // Pre-fill the form with calculated balances and first bank account
    setPaymentForm({
      ...paymentForm,
      paymentPeriod: {
        month: format(dateRange.startDate, 'yyyy-MM'),
        fromDate: fromDate,
        toDate: toDate,
        gstrPeriod: `GSTR-3B ${format(dateRange.startDate, 'MMM yyyy')}`
      },
      cgstPaid: balances.payable.cgst,
      sgstPaid: balances.payable.sgst,
      igstPaid: balances.payable.igst,
      bankAccount: bankAccounts.length > 0 ? bankAccounts[0]._id : ''
    });
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    dispatch(resetPaymentStatus());
  };

  const handlePaymentFormChange = (field: string, value: any) => {
    setPaymentForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReturnDetailsChange = (field: string, value: any) => {
    setPaymentForm((prev) => {
      const updatedReturnDetails = {
        ...prev.returnDetails,
        [field]: value
      };

      // Auto-update return type and frequency based on taxpayer type
      if (field === 'taxpayerType') {
        if (value === 'REGULAR') {
          updatedReturnDetails.returnType = 'GSTR-3B';
          updatedReturnDetails.paymentFrequency = 'MONTHLY';
        } else if (value === 'QRMP') {
          updatedReturnDetails.returnType = 'PMT-06';
          updatedReturnDetails.paymentFrequency = 'MONTHLY';
        } else if (value === 'COMPOSITION') {
          updatedReturnDetails.returnType = 'CMP-08';
          updatedReturnDetails.paymentFrequency = 'QUARTERLY';
        }
      }

      // Update gstrPeriod based on return type
      const returnType = field === 'returnType' ? value : updatedReturnDetails.returnType;
      const updatedPaymentPeriod = {
        ...prev.paymentPeriod,
        gstrPeriod: `${returnType} ${format(new Date(prev.paymentPeriod.fromDate), 'MMM yyyy')}`
      };

      return {
        ...prev,
        paymentPeriod: updatedPaymentPeriod,
        returnDetails: updatedReturnDetails
      };
    });
  };

  const handleSubmitPayment = (selectedPartyIds?: string[]) => {
    dispatch(recordGSTPayment({
      ...paymentForm,
      selectedPartyIds
    }));
  };

  // TDS Payment Dialog Handlers
  const handleOpenTDSPaymentDialog = () => {
    if (!tdsBalances) {
      return;
    }

    const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
    const toDate = format(dateRange.endDate, 'yyyy-MM-dd');

    setTDSPaymentForm({
      ...tdsPaymentForm,
      paymentPeriod: {
        month: format(dateRange.startDate, 'yyyy-MM'),
        fromDate: fromDate,
        toDate: toDate,
        period: `${format(dateRange.startDate, 'MMM yyyy')}`
      },
      tdsPayable: tdsBalances.payable,
      bankAccount: bankAccounts.length > 0 ? bankAccounts[0]._id : ''
    });
    setOpenTDSPaymentDialog(true);
  };

  const handleCloseTDSPaymentDialog = () => {
    setOpenTDSPaymentDialog(false);
    dispatch(resetTDSPaymentStatus());
  };

  const handleTDSPaymentFormChange = (field: string, value: any) => {
    setTDSPaymentForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTDSReturnDetailsChange = (field: string, value: any) => {
    setTDSPaymentForm((prev) => ({
      ...prev,
      returnDetails: {
        ...prev.returnDetails,
        [field]: value
      }
    }));
  };

  const handleSubmitTDSPayment = () => {
    dispatch(recordTDSPayment(tdsPaymentForm));
  };

  // TCS Payment Dialog Handlers
  const handleOpenTCSPaymentDialog = () => {
    if (!tcsBalances) {
      return;
    }

    const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
    const toDate = format(dateRange.endDate, 'yyyy-MM-dd');

    setTCSPaymentForm({
      ...tcsPaymentForm,
      paymentPeriod: {
        month: format(dateRange.startDate, 'yyyy-MM'),
        fromDate: fromDate,
        toDate: toDate,
        period: `${format(dateRange.startDate, 'MMM yyyy')}`
      },
      tcsPayable: tcsBalances.payable,
      bankAccount: bankAccounts.length > 0 ? bankAccounts[0]._id : ''
    });
    setOpenTCSPaymentDialog(true);
  };

  const handleCloseTCSPaymentDialog = () => {
    setOpenTCSPaymentDialog(false);
    dispatch(resetTCSPaymentStatus());
  };

  const handleTCSPaymentFormChange = (field: string, value: any) => {
    setTCSPaymentForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTCSReturnDetailsChange = (field: string, value: any) => {
    setTCSPaymentForm((prev) => ({
      ...prev,
      returnDetails: {
        ...prev.returnDetails,
        [field]: value
      }
    }));
  };

  const handleSubmitTCSPayment = () => {
    dispatch(recordTCSPayment(tcsPaymentForm));
  };

  const formatCurrency = (amount: number) => {
    // Handle NaN, undefined, null and convert to 0
    const validAmount = isNaN(amount) || amount === null || amount === undefined ? 0 : amount;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(validAmount);
  };

  // Bank accounts will be fetched via fetchBanks API in useEffect

  return (
    <Box sx={{ p: 2, mb: 2 }} className={'c-card2'}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="600">
          Tax Settlement (GST, TDS, TCS)
        </Typography>
      </Box>

      {/* Date Range Selector */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          bgcolor: 'white',
          borderRadius: 1
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              size="small"
              onClick={handleFetchBalances}
              disabled={balancesStatus === ApiStatus.LOADING}
            >
              Refresh
            </Button>
            <Select
              name="date"
              disableUnderline
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              sx={{
                boxShadow: "none",
                width: 250,
                pt: '0px !important',
                pl: '10px !important',
                '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                '& .MuiSelect-select': { p: 0 },
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
              }}
              className='c-text-field-3'
              variant="outlined"
              value={selectedDateRangeValue || ''}
              onChange={(e) => { handleDateRange(e) }}
            >
              <MenuItem value={'This Month'} key={'This Month'}> This Month </MenuItem>
              <MenuItem value={'Last Month'} key={'Last Month'}> Last Month </MenuItem>
              <MenuItem value={'This FY Year'} key={'This FY Year'} selected> This FY Year (Apr - Mar) </MenuItem>
              <MenuItem value={'Last FY Year'} key={'Last FY Year'} > Last FY Year (Apr - Mar) </MenuItem>
              <MenuItem value={'Custom'} key={'Custom'}> Custom Date </MenuItem>
            </Select>

            {showDateRange && (
              <>
                <DesktopDatePicker
                  inputFormat="dd/MM/yyyy"
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      fontSize: 14,
                      height: 28
                    },
                  }}
                  renderInput={(params) =>
                    <TextField {...params}
                      required
                      className='c-text-field-3'
                      variant="standard"
                      sx={{ width: 150 }}
                      size='small'
                      placeholder="Start Date"
                    />}
                  value={dateRange.startDate || null}
                  onChange={(value) => { handleDateChange(value, 'startDate') }}
                />
                <DesktopDatePicker
                  inputFormat="dd/MM/yyyy"
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      fontSize: 14,
                      height: 28
                    },
                  }}
                  renderInput={(params) =>
                    <TextField {...params}
                      required
                      className='c-text-field-3'
                      variant="standard"
                      sx={{ width: 150 }}
                      size='small'
                      placeholder="End Date"
                    />}
                  value={dateRange.endDate || null}
                  onChange={(value) => { handleDateChange(value, 'endDate') }}
                />
              </>
            )}

            <Button
              variant="contained"
              onClick={handleFetchBalances}
              disabled={balancesStatus === ApiStatus.LOADING}
              sx={{ ml: 2 }}
            >
              {balancesStatus === ApiStatus.LOADING ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Calculate GST'
              )}
            </Button>
          </Box>
        </LocalizationProvider>
      </Box>

      <Typography variant="h5" fontWeight="600" sx={{ mb: 4, backgroundColor: '#e9ecf1', p: 2, mx: -2}}>
        GST Settlement
      </Typography>

      {/* GST Summary */}
      {balances && (
        <>
          {/* First Row: GST Payable, GST Receivable, Net GST */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {/* GST Payable */}
            <Grid item xs={12} md={3.5}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: '2px solid #9da7b7',
                  borderRadius: 5,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" color="error.main" gutterBottom>
                  GST Payable (Output Tax)
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1" color="text.secondary">CGST</Typography>
                  <Typography variant="h5" fontWeight="600">{formatCurrency(balances.payable.cgst)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1" color="text.secondary">SGST</Typography>
                  <Typography variant="h5" fontWeight="600">{formatCurrency(balances.payable.sgst)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1" color="text.secondary">IGST</Typography>
                  <Typography variant="h5" fontWeight="600">{formatCurrency(balances.payable.igst)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1" color="text.secondary">TCS</Typography>
                  <Typography variant="h5" fontWeight="600">{formatCurrency(balances.payable.tcs)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="600">
                    Total:
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="error.main">
                    {formatCurrency(balances.payable.total)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* GST Receivable */}
            <Grid item xs={12} md={3.5}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: '2px solid #9da7b7',
                  borderRadius: 5,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" color="success.main" gutterBottom>
                  GST Receivable (Input Tax Credit)
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1" color="text.secondary">CGST</Typography>
                  <Typography variant="h5" fontWeight="600">{formatCurrency(balances.receivable.cgst)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1" color="text.secondary">SGST</Typography>
                  <Typography variant="h5" fontWeight="600">{formatCurrency(balances.receivable.sgst)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1" color="text.secondary">IGST</Typography>
                  <Typography variant="h5" fontWeight="600">{formatCurrency(balances.receivable.igst)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1" color="text.secondary">TCS</Typography>
                  <Typography variant="h5" fontWeight="600">{formatCurrency(balances.receivable.tcs)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="600">
                    Total:
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="success.main">
                    {formatCurrency(balances.receivable.total)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Net GST to Pay */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: balances.netPayable > 0 ? '3px solid #9da7b7' : '3px solid #2196f3',
                  borderRadius: 5,
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    Net GST {balances.netPayable > 0 ? 'to Pay' : 'Refundable'}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={handleOpenPaymentDialog}
                    color={balances.netPayable > 0 ? 'warning' : 'primary'}
                    size="small"
                  >
                    Record Payment
                  </Button>
                </Box>
                <Typography variant="h4" fontWeight="700" color={balances.netPayable > 0 ? 'warning.main' : 'info.main'} sx={{ my: 2 }}>
                  {formatCurrency(Math.abs(balances.netPayable))}
                </Typography>

                {/* Net GST Calculation Breakdown */}
                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" fontWeight="600" gutterBottom sx={{ color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Calculation Breakdown
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Component</Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ width: 70, textAlign: 'right' }}>CGST</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ width: 70, textAlign: 'right' }}>SGST</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ width: 70, textAlign: 'right' }}>IGST</Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" color="success.main" fontWeight="500">Input (ITC)</Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" fontWeight="500" sx={{ width: 70, textAlign: 'right' }}>{formatCurrency(balances.receivable.cgst)}</Typography>
                      <Typography variant="body2" fontWeight="500" sx={{ width: 70, textAlign: 'right' }}>{formatCurrency(balances.receivable.sgst)}</Typography>
                      <Typography variant="body2" fontWeight="500" sx={{ width: 70, textAlign: 'right' }}>{formatCurrency(balances.receivable.igst)}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" color="error.main" fontWeight="500">Output (Tax)</Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" fontWeight="500" sx={{ width: 70, textAlign: 'right' }}>−{formatCurrency(balances.payable.cgst)}</Typography>
                      <Typography variant="body2" fontWeight="500" sx={{ width: 70, textAlign: 'right' }}>−{formatCurrency(balances.payable.sgst)}</Typography>
                      <Typography variant="body2" fontWeight="500" sx={{ width: 70, textAlign: 'right' }}>−{formatCurrency(balances.payable.igst)}</Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="700" color={balances.netPayable > 0 ? 'warning.main' : 'info.main'}>
                      Net
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" fontWeight="700" sx={{ width: 70, textAlign: 'right' }}>
                        {formatCurrency(balances.receivable.cgst - balances.payable.cgst)}
                      </Typography>
                      <Typography variant="body2" fontWeight="700" sx={{ width: 70, textAlign: 'right' }}>
                        {formatCurrency(balances.receivable.sgst - balances.payable.sgst)}
                      </Typography>
                      <Typography variant="body2" fontWeight="700" sx={{ width: 70, textAlign: 'right' }}>
                        {formatCurrency(balances.receivable.igst - balances.payable.igst)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

              </Box>
            </Grid>
          </Grid>

          {/* Second Row: Data Breakdown */}
          {/* {balances.breakdown && (
            <Box sx={{ mb: 4, p: 2, bgcolor: 'white', borderRadius: 5, border: '1px solid #9da7b7' }}>
              <Typography variant="h5" fontWeight="600" gutterBottom>
                Data Breakdown
              </Typography>
             <Divider sx={{ my: 1 }} /> 
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, border: '1px solid #9da7b7', borderRadius: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="600" gutterBottom>
                      Old Invoices
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body1">Sales:</Typography>
                      <Typography variant="body1">{balances.breakdown.oldInvoices.salesCount} ({formatCurrency(balances.breakdown.oldInvoices.salesGST)})</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Purchase:</Typography>
                      <Typography variant="body1">{balances.breakdown.oldInvoices.purchaseCount} ({formatCurrency(balances.breakdown.oldInvoices.purchaseGST)})</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, border: '1px solid #9da7b7', borderRadius: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="600" gutterBottom>
                      New Journal Entries
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body1">Entries:</Typography>
                      <Typography variant="body1">{balances.breakdown.newJournalEntries.journalEntriesCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Sales:</Typography>
                      <Typography variant="body1">{formatCurrency(balances.breakdown.newJournalEntries.salesGST)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Purchase:</Typography>
                      <Typography variant="body1">{formatCurrency(balances.breakdown.newJournalEntries.purchaseGST)}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2,  border: '1px solid #9da7b7', borderRadius: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="600" gutterBottom>
                      Previous Payments
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body1">Count:</Typography>
                      <Typography variant="body1">{balances.breakdown.paymentsAlreadyMade.count}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Total Paid:</Typography>
                      <Typography variant="body1">{formatCurrency(balances.breakdown.paymentsAlreadyMade.totalPaid)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )} 
          */}
        </>
      )}

      {/* Payment History */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'white',
          border: '1px solid #9da7b7',
          borderRadius: 5
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" fontWeight="600">
            Payment History
          </Typography>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            size="small"
            onClick={() => dispatch(fetchGSTPaymentHistory())}
          >
            Refresh History
          </Button>
        </Box>
        <Divider sx={{ my: 1 }} />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Txn ID</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Payment Date</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Return Type</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Challan No.</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>CGST</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>SGST</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>IGST</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Total Paid</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyStatus === ApiStatus.LOADING ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No payment records found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow key={payment._id} hover>
                    <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.txnId}</TableCell>
                    <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{format(new Date(payment.paymentDate), 'dd MMM yyyy')}</TableCell>
                    <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.paymentPeriod.gstrPeriod || payment.paymentPeriod.month}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={payment.returnDetails?.returnType || '-'}
                        size="medium"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.challanNumber || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{formatCurrency(payment.cgstPaid)}</TableCell>
                    <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{formatCurrency(payment.sgstPaid)}</TableCell>
                    <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{formatCurrency(payment.igstPaid)}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem', py: 1.5 }}>{formatCurrency(payment.totalPaid)}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={payment.status}
                        color={payment.status === 'PAID' ? 'success' : 'default'}
                        size="medium"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ============================================ */}
      {/* TDS SETTLEMENT SECTION */}
      {/* ============================================ */}
      <Box sx={{ mt: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, backgroundColor: '#e9ecf1', p: 2, mx: -2 }}>
          <Typography variant="h5" fontWeight="600">
            TDS Settlement
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setOpenTDSTransactionModal(true)}
          >
            Transaction Summary
          </Button>
        </Box>

        {/* TDS Summary */}
        {tdsBalances && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: '2px solid #9da7b7',
                  borderRadius: 5,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" color="error.main" gutterBottom>
                  TDS Payable (To Government)
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h4" fontWeight="700" color="error.main">
                  {formatCurrency(tdsBalances.payable)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: '2px solid #9da7b7',
                  borderRadius: 5,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" color="success.main" gutterBottom>
                  TDS Receivable (From Parties)
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h4" fontWeight="700" color="success.main">
                  {formatCurrency(tdsBalances.receivable)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: '3px solid #9da7b7',
                  borderRadius: 5,
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    Net TDS to Pay
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={handleOpenTDSPaymentDialog}
                    color="warning"
                    size="small"
                    disabled={tdsBalances.payable <= 0}
                  >
                    Record Payment
                  </Button>
                </Box>
                <Typography variant="h4" fontWeight="700" color="warning.main">
                  {formatCurrency(tdsBalances.payable)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* TDS Payment History */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'white',
            border: '1px solid #9da7b7',
            borderRadius: 5
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight="600">
              TDS Payment History
            </Typography>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              size="small"
              onClick={() => dispatch(fetchTDSPaymentHistory())}
            >
              Refresh History
            </Button>
          </Box>
          <Divider sx={{ my: 1 }} />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Txn ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Payment Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Section</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Form Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Challan No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>BSR Code</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>TDS Paid</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tdsHistoryStatus === ApiStatus.LOADING ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : tdsPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No TDS payment records found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tdsPayments.map((payment) => (
                    <TableRow key={payment._id} hover>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.txnId}</TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{format(new Date(payment.paymentDate), 'dd MMM yyyy')}</TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.paymentPeriod.period || payment.paymentPeriod.month}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={payment.returnDetails?.section || '-'}
                          size="medium"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.returnDetails?.formType || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.challanNumber || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.bsrCode || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem', py: 1.5 }}>{formatCurrency(payment.tdsPayable)}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={payment.status}
                          color={payment.status === 'PAID' ? 'success' : 'default'}
                          size="medium"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* ============================================ */}
      {/* TCS SETTLEMENT SECTION */}
      {/* ============================================ */}
      <Box sx={{ mt: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, backgroundColor: '#e9ecf1', p: 2, mx: -2 }}>
          <Typography variant="h5" fontWeight="600">
            TCS Settlement
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setOpenTCSTransactionModal(true)}
          >
            Transaction Summary
          </Button>
        </Box>

        {/* TCS Summary */}
        {tcsBalances && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: '2px solid #9da7b7',
                  borderRadius: 5,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" color="error.main" gutterBottom>
                  TCS Payable (To Government)
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h4" fontWeight="700" color="error.main">
                  {formatCurrency(tcsBalances.payable)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: '2px solid #9da7b7',
                  borderRadius: 5,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" color="info.main" gutterBottom>
                  TCS Paid (Already Remitted)
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h4" fontWeight="700" color="info.main">
                  {formatCurrency(tcsBalances.paid)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: '3px solid #9da7b7',
                  borderRadius: 5,
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    Net TCS to Pay
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={handleOpenTCSPaymentDialog}
                    color="warning"
                    size="small"
                    disabled={tcsBalances.payable <= 0}
                  >
                    Record Payment
                  </Button>
                </Box>
                <Typography variant="h4" fontWeight="700" color="warning.main">
                  {formatCurrency(tcsBalances.payable)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* TCS Payment History */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'white',
            border: '1px solid #9da7b7',
            borderRadius: 5
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight="600">
              TCS Payment History
            </Typography>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              size="small"
              onClick={() => dispatch(fetchTCSPaymentHistory())}
            >
              Refresh History
            </Button>
          </Box>
          <Divider sx={{ my: 1 }} />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Txn ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Payment Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Section</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Form Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Challan No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>BSR Code</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>TCS Paid</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tcsHistoryStatus === ApiStatus.LOADING ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : tcsPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No TCS payment records found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tcsPayments.map((payment) => (
                    <TableRow key={payment._id} hover>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.txnId}</TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{format(new Date(payment.paymentDate), 'dd MMM yyyy')}</TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.paymentPeriod.period || payment.paymentPeriod.month}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={payment.returnDetails?.section || '-'}
                          size="medium"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.returnDetails?.formType || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.challanNumber || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.bsrCode || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem', py: 1.5 }}>{formatCurrency(payment.tcsPayable)}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={payment.status}
                          color={payment.status === 'PAID' ? 'success' : 'default'}
                          size="medium"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Payment Dialogs */}
      <GSTPaymentDialog
        open={openPaymentDialog}
        onClose={handleClosePaymentDialog}
        paymentForm={paymentForm}
        onPaymentFormChange={handlePaymentFormChange}
        onReturnDetailsChange={handleReturnDetailsChange}
        onSubmit={handleSubmitPayment}
        paymentStatus={paymentStatus}
        bankLedgers={bankAccounts}
      />

      <TDSPaymentDialog
        open={openTDSPaymentDialog}
        onClose={handleCloseTDSPaymentDialog}
        paymentForm={tdsPaymentForm}
        onPaymentFormChange={handleTDSPaymentFormChange}
        onReturnDetailsChange={handleTDSReturnDetailsChange}
        onSubmit={handleSubmitTDSPayment}
        paymentStatus={tdsPaymentStatus}
        bankLedgers={bankAccounts}
      />

      <TCSPaymentDialog
        open={openTCSPaymentDialog}
        onClose={handleCloseTCSPaymentDialog}
        paymentForm={tcsPaymentForm}
        onPaymentFormChange={handleTCSPaymentFormChange}
        onReturnDetailsChange={handleTCSReturnDetailsChange}
        onSubmit={handleSubmitTCSPayment}
        paymentStatus={tcsPaymentStatus}
        bankLedgers={bankAccounts}
      />

      {/* Transaction Summary Modals */}
      <TDSTransactionSummaryModal
        open={openTDSTransactionModal}
        onClose={() => setOpenTDSTransactionModal(false)}
        fromDate={format(dateRange.startDate, 'yyyy-MM-dd')}
        toDate={format(dateRange.endDate, 'yyyy-MM-dd')}
      />

      <TCSTransactionSummaryModal
        open={openTCSTransactionModal}
        onClose={() => setOpenTCSTransactionModal(false)}
        fromDate={format(dateRange.startDate, 'yyyy-MM-dd')}
        toDate={format(dateRange.endDate, 'yyyy-MM-dd')}
      />
    </Box>
  );
};

export default GSTSettlement;
